import { atom } from 'jotai';
import { atomWithStorage } from 'jotai/utils';
import type { ChatMessage, SuggestionChip } from 'src/chat/types';

export const chatOpenAtom = atomWithStorage<boolean>('fs.chat.open', false);

/**
 * Tracks whether we have already applied the "open chat by default" behaviour
 * once for this browser. Persists across sign-out/sign-in so a user's explicit
 * close in a previous session is honoured on subsequent logins.
 */
export const chatOpenDefaultedAtom = atomWithStorage<boolean>(
  'fs.chat.openDefaulted',
  false,
);

/**
 * #932: one-shot "draw attention to the AI Agent" state. True once the user
 * has dismissed the coach mark or sent their first AI prompt — the header
 * pulse and coach mark never show again for this browser.
 */
export const aiAttentionDismissedAtom = atomWithStorage<boolean>(
  'fs.ai.attentionDismissed',
  false,
);

/**
 * Bumped when something outside the panel wants the composer focused without
 * changing its contents — e.g. the "Ask about these…" nudge, which opens the
 * panel with the selection in context but no draft. Writing an empty
 * `pendingChipAtom` would wipe a draft the user had already typed.
 */
export const chatFocusRequestAtom = atom<number>(0);

export const chatMessagesAtom = atom<ChatMessage[]>([]);
export const chatStreamingAtom = atom(false);
export const chatSessionIdAtom = atom<string | null>(null);

/**
 * The current in-flight turn id, set from the `turn_id` SSE event at the
 * start of each stream and cleared when the stream ends. The Stop button
 * reads it to call the proxy's cancel endpoint. Distinct from the session
 * id: a session spans many turns, this identifies the single live turn.
 */
export const chatTurnIdAtom = atom<string | null>(null);

/**
 * Bumped by anything that wants the panel to start a FRESH agent session:
 * the header's "New chat" button and every prompt-carrying nudge pill.
 *
 * A request rather than a direct write, because a correct reset needs two
 * things only the sidebar owns: the AbortController for an in-flight turn
 * (it lives inside the `useChatSend` instance that started it) and the
 * pending auto-save debounce, which must be flushed or the last turn of the
 * discarded conversation is never persisted. `useChatSessionReset` performs
 * it; producers only signal intent.
 */
export const chatSessionResetRequestAtom = atom(0);

export const chatHistoryViewAtom = atom(false);
export const chatSessionOriginAtom = atom<string | null>(null);
export const chatSessionScheduleIdAtom = atom<string | null>(null);

// pendingChipAtom carries a one-shot chip from a producer (the chat empty
// state, "Browse all", the nudge rows) into ChatInput, which prefills or —
// for `sendNow` chips — sends it, then clears the atom. Kept in shared so
// producers rendered inside MessageList don't drill callbacks back up
// through ChatSidebar.
export const pendingChipAtom = atom<SuggestionChip | null>(null);

/**
 * Content types of assets the UI has listed this session, by id. Written by
 * the pages that render search results (Dashboard, Basket) so the chat can
 * tell the agent — and the starter chips — what a selection is. The
 * ['asset', id] query cache only fills once a detail modal opens, so on its
 * own it reports 'unknown' for most of a selection.
 */
export const assetContentTypesAtom = atom<Record<string, string>>({});

/**
 * @deprecated Use `applyAiSearchIntentAtom` from
 *   `src/shared/dashboardFilters.ts`. The bridge atom is no longer written
 *   to by the chat sidebar — both `useSSEStream.ts` and `ActionCard.tsx`
 *   write the intent straight onto the canonical dashboard filter store.
 *   Kept exported for one release in case any external consumer reads it.
 */
export interface AiSearchIntent {
  keyword?: string;
  content_type?: string;
  file_name?: string;
  status?: string;
  upload_time_range?: { start?: string; end?: string };
  sort_by?: string[];
  schema_criteria?: { data_schema_id: number; fields?: Record<string, any>; field?: object };
  timestamp: number;
}

/** @deprecated See `AiSearchIntent`. */
export const aiSearchIntentAtom = atom<AiSearchIntent | null>(null);

function stripTrailingWildcard(val: unknown): unknown {
  if (typeof val === 'string' && val.endsWith('*')) return val.slice(0, -1);
  return val;
}

/**
 * Strips trailing `*` from string values in query_criteria,
 * except values inside `schema_criteria` which preserve wildcards as user-facing syntax.
 */
export function stripQueryCriteriaWildcards(qc: Record<string, any>): Record<string, any> {
  const out: Record<string, any> = {};
  for (const [key, val] of Object.entries(qc)) {
    if (key === 'schema_criteria') {
      out[key] = val;
    } else if (val && typeof val === 'object' && !Array.isArray(val)) {
      out[key] = stripQueryCriteriaWildcards(val);
    } else if (Array.isArray(val)) {
      out[key] = val.map(stripTrailingWildcard);
    } else {
      out[key] = stripTrailingWildcard(val);
    }
  }
  return out;
}
